import React from 'react'
import ChatBubbleIcon from '@material-ui/icons/ChatBubble'
import times from 'lodash.times'
import { Button, FormControlLabel, Checkbox, makeStyles, TextField, useTheme } from '@material-ui/core'

import { addRoll } from '../../api/rolling'
import { DIE_TYPES } from '../../constants'
import Die from './Die'
import { computeResult, getDieRoll } from './util'
import D100 from './D100'

const useStyles = makeStyles((theme) => ({
  dieArea: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: -4,
    marginRight: -4
  },
  submit: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    flexGrow: 1,
    fontSize: 16
  },
  submitContainer: {
    display: 'flex',
    marginTop: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  checkboxRoot: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.text.secondary,
    borderRadius: 4,
    marginLeft: 8,
    marginRight: 0,
    paddingRight: 9
  },
  checkboxLabel: {
    fontSize: 14,
    lineHeight: 1
  }
}))

const DicePool = ({ campaignId, characterName, dicePool, loading, setDicePool, skillIndex, setSkillIndex, onSetDestinyPool }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isDarkMode = theme.palette.type === 'dark'

  const [ rollInProgress, setRollInProgress ] = React.useState(false)
  const [ shouldSavePool, setShouldSavePool ] = React.useState(false)
  const [ caption, setCaption ] = React.useState(false)

  const onDieChange = React.useCallback((key, value) => {
    const newPool = { ...dicePool, [key]: value }
    if (value === 0) delete newPool[key]
    if (key === 'd100' && value?.amount === 0) delete newPool[key]
    setDicePool(newPool)
  }, [ dicePool, setDicePool ])

  const onRoll = async () => {
    const roll = Object.keys(dicePool)
      // Filter out dice types where there are no dice set (i.e. number of dice is 0)
      .filter((diceType) => dicePool[diceType] > 0 || typeof dicePool[diceType] === 'object')
      .map((diceType) => {
        const isD100 = diceType === 'd100'
        const numberOfDice = isD100 ? dicePool[diceType].amount : dicePool[diceType]
        const rollResults = []

        // For each die, roll it, and return the side of the die it landed on
        for (let i = 0; i < numberOfDice; i++) {
          const roll = getDieRoll(diceType, dicePool[diceType].num)
          rollResults.push(isD100 ? { num: dicePool[diceType].num, roll } : roll)
        }

        return { diceType, rollResults }
      })
      // Put it back into an object for easier processing later.
      .reduce((collector, curr) => {
        collector[curr.diceType] = curr.rollResults
        return collector
      }, {})

    // Ignore empty rolls
    if (Object.keys(roll).length === 0) return

    const isDestinyRoll = Object.prototype.hasOwnProperty.call(roll, 'destiny')

    try {
      setRollInProgress(true)
      await addRoll(campaignId, characterName ? characterName : 'GM', roll, isDestinyRoll ? 'Automatically added results to destiny pool' : caption, skillIndex)

      // process any destiny dice here and auto-add them to destiny pool
      if (isDestinyRoll) {
        const { n, l } = computeResult(roll)

        const darkResult = times(n).map(() => 'addDark')
        const lightResult = times(l).map(() => 'addLight')

        if (darkResult.length > 0 || lightResult.length > 0) {
          await onSetDestinyPool('multi', [ ...darkResult, ...lightResult ])
        }
      }
    } catch(e) {
      console.error(e)
      window.alert('There was a problem processing your roll.')
    } finally {
      if (!shouldSavePool) {
        setDicePool({})
        setSkillIndex(false)
        setCaption(false)
      }
      setRollInProgress(false)
    }
  }

  return (
    <div>
      <div className={classes.dieArea}>
        {Object.keys(DIE_TYPES).map((type) => {
          const isDestiny = type === 'destiny'
          const disabled = isDestiny ?
            Object.keys(dicePool).filter((name) => name !== 'destiny').length > 0
            :
            Object.prototype.hasOwnProperty.call(dicePool, 'destiny')
          return (
            <Die
              amount={dicePool[type]}
              disabled={disabled}
              key={type} type={type}
              isDarkMode={isDarkMode}
              onDieChange={onDieChange.bind(this, type)} />
          )
        })}
        <D100 info={dicePool.d100} onDieChange={onDieChange} />
      </div>

      <div className={classes.submitContainer}>
        <Button
          disabled={loading || Object.keys(dicePool).length === 0 || rollInProgress}
          disableElevation
          type='button'
          variant='contained'
          color='primary'
          className={classes.submit}
          onClick={onRoll}
        >
          Roll
        </Button>
        <FormControlLabel
          classes={{ root: classes.checkboxRoot, label: classes.checkboxLabel }}
          control={
            <Checkbox color='primary' checked={shouldSavePool} onChange={() => setShouldSavePool(!shouldSavePool)} name='checkedA' />
          }
          label='Save Roll'
        />
        <Button
          disabled={Object.keys(dicePool).length === 0 || rollInProgress}
          disableElevation
          type='button'
          variant='contained'
          color='secondary'
          css={`
            padding: 10px 6px;
            margin-left: 8px;
          `}
          onClick={() => {
            setDicePool({})
            setShouldSavePool(false)
            setCaption(false)
            setSkillIndex(false)
          }}
        >
          Reset
        </Button>
        <Button
          disabled={Object.keys(dicePool).length === 0 || rollInProgress}
          disableElevation
          type='button'
          variant='contained'
          color='secondary'
          title='Add a description to your roll'
          css={`
            min-width: unset;
            padding: 10px 6px;
            margin-left: 8px;
          `}
          onClick={() => setCaption(typeof caption === 'string' ? false : '')}
        >
          <ChatBubbleIcon />
        </Button>
      </div>

      {typeof caption === 'string' &&
        <div css={`display: flex; margin-top: 12px;`}>
          <TextField variant='outlined' size='small' onChange={({ target }) => {
            setCaption(target.value)
          }} placeholder='Enter a description for your roll' fullWidth />
        </div>
      }
    </div>
  )
}

export default DicePool
